/**
 * The flow editor's glyphs — ported one for one from the concept's
 * `flow/icons.jsx`.
 *
 * Why not lucide, which the rest of the app uses: these are the reference
 * editor's own drawings (the split's forked arrow, the condition's diamond with
 * a question mark, the Start disc), and the editor is meant to read as that
 * reference. Lucide's nearest equivalents are close but not the same marks, and
 * a canvas that half-matches its design is worse than either. Stroke
 * `currentColor`, 1.8 wide, decorative everywhere (`aria-hidden`): every
 * control that shows one also carries an accessible name of its own.
 */
import type { ReactNode } from 'react';

export interface IconProps {
  size?: number;
  className?: string;
}

function Svg({ size = 16, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

type Icon = (props: IconProps) => ReactNode;

export const IcClose: Icon = (p) => <Svg {...p}><path d="M6 6l12 12M18 6L6 18" /></Svg>;
export const IcPencil: Icon = (p) => <Svg {...p}><path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4" /></Svg>;
export const IcChat: Icon = (p) => <Svg {...p}><path d="M20.5 11.6a8.4 8.4 0 0 1-12.3 7.4L3.5 20.4l1.4-4.5A8.4 8.4 0 1 1 20.5 11.6z" /></Svg>;
export const IcMail: Icon = (p) => <Svg {...p}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3.5 7.5l8.5 6 8.5-6" /></Svg>;
export const IcFile: Icon = (p) => <Svg {...p}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></Svg>;
export const IcFileLogic: Icon = (p) => <Svg {...p}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" /><path d="M14 3v5h5M9 12.5l2 2 4-4" /></Svg>;
export const IcSplit: Icon = (p) => <Svg {...p}><path d="M3 12h5.5M8.5 12L14 6h6M8.5 12L14 18h6M17.5 3.5L20 6l-2.5 2.5M17.5 15.5L20 18l-2.5 2.5" /></Svg>;
export const IcClock: Icon = (p) => <Svg {...p}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></Svg>;
export const IcCond: Icon = (p) => <Svg {...p}><path d="M12 3.5l8.5 8.5-8.5 8.5L3.5 12z" /><path d="M10.3 10a1.8 1.8 0 1 1 2.4 1.7c-.5.2-.7.6-.7 1.1v.4M12 15.8v.1" /></Svg>;
export const IcPerson: Icon = (p) => <Svg {...p}><circle cx="10" cy="8" r="3.8" /><path d="M3.5 20c.9-3.6 3.4-5.6 6.5-5.6 1.4 0 2.6.4 3.6 1.1M16 15.5v5M13.5 18h5" /></Svg>;
export const IcFlag: Icon = (p) => <Svg {...p}><path d="M5 21V4M5 4.5h11.5l-2.2 4 2.2 4H5" /></Svg>;
export const IcExit: Icon = (p) => <Svg {...p}><path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4M15 8l4 4-4 4M19 12H9.5" /></Svg>;
export const IcComment: Icon = (p) => <Svg {...p}><path d="M4 5.5h16v10.5H10l-4.5 3.5V16H4z" /><path d="M8 9.5h8M8 12.5h5" /></Svg>;
export const IcSpark: Icon = (p) => <Svg {...p}><path d="M11 3.5l1.7 4.8 4.8 1.7-4.8 1.7L11 16.5l-1.7-4.8-4.8-1.7 4.8-1.7z" /><path d="M18 14.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" /></Svg>;
export const IcCheck: Icon = (p) => <Svg {...p}><path d="M5 12.5l4.5 4.5L19 7.5" /></Svg>;
export const IcStart: Icon = (p) => <Svg {...p}><circle cx="12" cy="12" r="8.5" /><path d="M10 8.6v6.8l5.4-3.4z" /></Svg>;
export const IcSend: Icon = (p) => <Svg {...p}><path d="M20.5 3.5L3.5 10.6l6.8 2.6 2.6 6.8z" /><path d="M10.3 13.2l4.5-4.5" /></Svg>;
export const IcPause: Icon = (p) => <Svg {...p}><path d="M9 5.5v13M15 5.5v13" /></Svg>;
export const IcPlay: Icon = (p) => <Svg {...p}><path d="M8 5.5v13l10-6.5z" /></Svg>;
export const IcPlus: Icon = (p) => <Svg {...p}><path d="M12 5v14M5 12h14" /></Svg>;
export const IcMinus: Icon = (p) => <Svg {...p}><path d="M5 12h14" /></Svg>;
export const IcFlask: Icon = (p) => <Svg {...p}><path d="M9 3.5h6M10 3.5v6L4.8 18a2 2 0 0 0 1.7 3h11a2 2 0 0 0 1.7-3L14 9.5v-6M7.5 14.5h9" /></Svg>;
export const IcChevron: Icon = (p) => <Svg {...p}><path d="M6 9l6 6 6-6" /></Svg>;
export const IcBack: Icon = (p) => <Svg {...p}><path d="M15 6l-6 6 6 6" /></Svg>;
export const IcNext: Icon = (p) => <Svg {...p}><path d="M9 6l6 6-6 6" /></Svg>;
export const IcLock: Icon = (p) => <Svg {...p}><rect x="5" y="10.5" width="14" height="10" rx="2" /><path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" /></Svg>;
export const IcWarn: Icon = (p) => <Svg {...p}><path d="M12 4l9 16H3z" /><path d="M12 10v4.5M12 17.2v.1" /></Svg>;
export const IcFit: Icon = (p) => <Svg {...p}><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></Svg>;
export const IcTrash: Icon = (p) => <Svg {...p}><path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 13h9l1-13" /></Svg>;
export const IcRestart: Icon = (p) => <Svg {...p}><path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3M4.5 4.5v4h4" /></Svg>;
export const IcLink: Icon = (p) => <Svg {...p}><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></Svg>;
